import { Volume2, Play, Pause, SkipBack, SkipForward, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { Track } from "@/types";
import { useIsMobile } from "@/hooks/useResponsive";
import { getTrackCoverUrl, handleImageError } from "@/utils/trackCoverUtils";

interface PlayerCollapsedViewProps {
  currentTrack: Track;
  isPlaying: boolean;
  liked: boolean;
  sponsorName?: string | null;
  sponsorUsername?: string | null;
  volume: number;
  currentTime: number;
  audioRef: React.RefObject<HTMLAudioElement>;
  onToggleLike: () => void;
  onTogglePlayPause: () => void;
  onVolumeChange: (value: number[]) => void;
  onSeek: (time: number[]) => void;
  onSeekToTime: (time: number) => void;
  onToggleExpand: () => void;
  formatTime: (time: number) => string;
  setIsPlaying: (playing: boolean) => void;
}

export function PlayerCollapsedView({
  currentTrack,
  isPlaying,
  sponsorName,
  volume,
  currentTime,
  audioRef,
  onTogglePlayPause,
  onVolumeChange,
  onSeekToTime,
  onToggleExpand,
  formatTime,
}: PlayerCollapsedViewProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const duration = audioRef.current?.duration || 0;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleCoverClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/tracks/${currentTrack.slug || currentTrack.id}`);
  };

  const handleProgressClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newTime = (percentage / 100) * duration;
    onSeekToTime(newTime);
  };

  // Mobile collapsed view
  if (isMobile) {
    return (
      <div
        className="w-full bg-[#0E0C24]/95 backdrop-blur-md border-t border-white/[0.16] cursor-pointer active:scale-[0.995] transition-transform font-archivo"
        onClick={onToggleExpand}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Progress Bar - top accent */}
        <div
          className="w-full h-[2px] bg-white/[0.16] cursor-pointer"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-[#8B6BF0] rounded-r-full transition-all duration-150 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex items-center gap-2.5 px-3 py-1.5">
          {/* Album Art — navigates to the track page, doesn't expand the player */}
          <div className="relative flex-shrink-0" onClick={handleCoverClick}>
            <div className="w-9 h-9 rounded-[3px] overflow-hidden">
              <img
                src={getTrackCoverUrl(currentTrack.coverUrl)}
                alt={currentTrack.title}
                className="w-full h-full object-cover"
                onError={handleImageError}
              />
            </div>
          </div>

          {/* Track Info */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-[13px] truncate leading-tight">
              {currentTrack.title}
            </p>
            <p className="text-[#9A96C4] text-[11px] truncate leading-tight mt-0.5">
              {currentTrack.artist}
            </p>
          </div>

          {/* Play/Pause only */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePlayPause();
            }}
            className="text-[#0E0C24] p-0 h-9 w-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
          >
            {isPlaying ? (
              <Pause className="h-[15px] w-[15px]" />
            ) : (
              <Play className="h-[15px] w-[15px] ml-0.5" />
            )}
          </button>
        </div>
      </div>
    );
  }

  // Desktop collapsed view — single row with dominant progress bar
  return (
    <div
      className="bg-[#0E0C24]/95 backdrop-blur-md border-t border-white/[0.08] font-archivo"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-5 px-6 py-3.5 max-w-7xl mx-auto">
        {/* Left: Art (navigates to track) + Info (click to expand) */}
        <div className="flex items-center gap-3.5 min-w-0 w-[240px] flex-shrink-0">
          <div
            className="w-11 h-11 rounded-[3px] overflow-hidden flex-shrink-0 bg-[#241F52] cursor-pointer group"
            onClick={handleCoverClick}
          >
            <img
              src={getTrackCoverUrl(currentTrack.coverUrl)}
              alt={currentTrack.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              onError={handleImageError}
            />
          </div>
          <div className="min-w-0 cursor-pointer" onClick={onToggleExpand}>
            <p className="text-white text-sm font-semibold truncate leading-tight">
              {currentTrack.title}
            </p>
            <p className="text-[#9A96C4] text-xs truncate leading-tight mt-0.5">
              {currentTrack.artist}
            </p>
          </div>
        </div>

        {/* Transport */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => console.log("Previous track - not implemented yet")}
            className="text-[#9A96C4] hover:text-white transition-colors"
            title="Previous track"
          >
            <SkipBack className="h-[18px] w-[18px]" />
          </button>
          <button
            onClick={onTogglePlayPause}
            className="h-10 w-10 rounded-full bg-white flex items-center justify-center text-[#0E0C24] flex-shrink-0"
          >
            {isPlaying ? (
              <Pause className="h-[17px] w-[17px]" />
            ) : (
              <Play className="h-[17px] w-[17px] ml-0.5" />
            )}
          </button>
          <button
            onClick={() => console.log("Next track - not implemented yet")}
            className="text-[#9A96C4] hover:text-white transition-colors"
            title="Next track"
          >
            <SkipForward className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Center: Progress bar (dominant) */}
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <span className="text-[#9A96C4] text-xs font-jetbrains tabular-nums w-9 text-right flex-shrink-0">
            {formatTime(currentTime)}
          </span>
          <div
            className="flex-1 h-1 bg-white/[0.16] rounded-full cursor-pointer group relative"
            onClick={handleProgressClick}
          >
            <div
              className="h-full bg-[#8B6BF0] rounded-full transition-all duration-150 ease-linear"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 5px)` }}
            />
          </div>
          <span className="text-[#9A96C4] text-xs font-jetbrains tabular-nums w-9 flex-shrink-0">
            {formatTime(duration)}
          </span>
        </div>

        {/* Right: Sponsor credit + Volume */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {currentTrack.isSponsored && sponsorName && (
            <span className="flex items-center gap-2 text-xs text-[#9A96C4]">
              <span className="font-bold text-[10px] tracking-wider text-[#E9B949] border border-[#E9B949]/45 rounded-[2px] px-1.5 py-1">
                AD
              </span>
              Sponsored by {sponsorName}
            </span>
          )}

          <div className="flex items-center gap-2">
            <Volume2 className="h-[15px] w-[15px] text-[#9A96C4]" />
            <Slider
              value={[volume]}
              max={100}
              step={1}
              onValueChange={onVolumeChange}
              className="w-16"
            />
          </div>

          <button
            onClick={onToggleExpand}
            className="text-[#9A96C4] hover:text-white transition-colors"
            title="Expand player"
          >
            <ChevronDown className="h-[18px] w-[18px] rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
}
